<script setup lang="ts">
import { ref, computed } from 'vue';
import type { Screenshot } from '@/data/help';

const props = defineProps<{
  screenshot: Screenshot;
}>();

const showAnnotated = ref(false);
const isZoomed = ref(false);
const imageLoaded = ref(false);
const imageError = ref(false);

const hasAnnotated = computed(() => !!props.screenshot.srcAnnotated);

const currentSrc = computed(() => {
  if (showAnnotated.value && props.screenshot.srcAnnotated) {
    return props.screenshot.srcAnnotated;
  }
  return props.screenshot.src;
});

// Build full path - screenshots are in public/help/screenshots/
// Use import.meta.env.BASE_URL to handle /app/ prefix in production
const imagePath = computed(() => {
  const base = import.meta.env.BASE_URL || '/';
  return `${base}help/screenshots/${currentSrc.value}`;
});

function openZoom() {
  isZoomed.value = true;
}

function closeZoom() {
  isZoomed.value = false;
}

function handleImageLoad() {
  imageLoaded.value = true;
  imageError.value = false;
}

function handleImageError() {
  imageLoaded.value = true;
  imageError.value = true;
}
</script>

<template>
  <figure class="help-screenshot">
    <!-- Image Container -->
    <div class="screenshot-container" @click="openZoom">
      <div v-if="!imageLoaded" class="screenshot-loading">
        <i class="bi bi-image"></i>
        <span>Loading image...</span>
      </div>
      <div v-else-if="imageError" class="screenshot-error">
        <i class="bi bi-exclamation-triangle"></i>
        <span>Image not found</span>
      </div>
      <img
        v-show="imageLoaded && !imageError"
        :src="imagePath"
        :alt="screenshot.alt"
        class="screenshot-image"
        @load="handleImageLoad"
        @error="handleImageError"
      />
      <div class="screenshot-overlay">
        <i class="bi bi-zoom-in"></i>
        <span>Click to zoom</span>
      </div>
    </div>

    <!-- Toggle Button -->
    <div v-if="hasAnnotated" class="screenshot-toggle">
      <button
        class="toggle-btn"
        :class="{ active: !showAnnotated }"
        @click="showAnnotated = false"
      >
        Plain
      </button>
      <button
        class="toggle-btn"
        :class="{ active: showAnnotated }"
        @click="showAnnotated = true"
      >
        Annotated
      </button>
    </div>

    <!-- Caption -->
    <figcaption v-if="screenshot.caption" class="screenshot-caption">
      {{ screenshot.caption }}
    </figcaption>

    <!-- Callouts Legend -->
    <div v-if="showAnnotated && screenshot.callouts?.length" class="screenshot-callouts">
      <div
        v-for="callout in screenshot.callouts"
        :key="callout.number"
        class="callout-item"
      >
        <span class="callout-number">{{ callout.number }}</span>
        <div class="callout-text">
          <strong>{{ callout.label }}</strong>
          <span v-if="callout.description"> - {{ callout.description }}</span>
        </div>
      </div>
    </div>

    <!-- Zoom Modal -->
    <Teleport to="body">
      <Transition name="zoom">
        <div v-if="isZoomed" class="zoom-overlay" @click="closeZoom">
          <div class="zoom-content" @click.stop>
            <button class="zoom-close" @click="closeZoom">
              <i class="bi bi-x-lg"></i>
            </button>
            <img
              :src="imagePath"
              :alt="screenshot.alt"
              class="zoom-image"
            />
            <div v-if="screenshot.caption" class="zoom-caption">
              {{ screenshot.caption }}
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </figure>
</template>

<style scoped>
.help-screenshot {
  margin: 16px 0;
}

.screenshot-container {
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  background: #f9fafb;
}

.screenshot-loading,
.screenshot-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #9ca3af;
}

.screenshot-loading i,
.screenshot-error i {
  font-size: 32px;
  margin-bottom: 8px;
}

.screenshot-error {
  color: #ef4444;
}

.screenshot-image {
  display: block;
  width: 100%;
  height: auto;
}

.screenshot-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  opacity: 0;
  transition: opacity 0.2s;
}

.screenshot-container:hover .screenshot-overlay {
  opacity: 1;
}

.screenshot-overlay i {
  font-size: 24px;
}

.screenshot-overlay span {
  font-size: 13px;
}

.screenshot-toggle {
  display: flex;
  gap: 4px;
  margin-top: 8px;
}

.toggle-btn {
  padding: 4px 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  font-size: 12px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s;
}

.toggle-btn:first-child {
  border-radius: 4px 0 0 4px;
}

.toggle-btn:last-child {
  border-radius: 0 4px 4px 0;
  border-left: none;
}

.toggle-btn:hover {
  background: #f3f4f6;
}

.toggle-btn.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}

.screenshot-caption {
  margin-top: 8px;
  font-size: 13px;
  color: #6b7280;
  font-style: italic;
}

.screenshot-callouts {
  margin-top: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.callout-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.callout-item:last-child {
  margin-bottom: 0;
}

.callout-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: #ef4444;
  color: white;
  font-size: 11px;
  font-weight: 600;
  border-radius: 50%;
  flex-shrink: 0;
}

.callout-text {
  font-size: 13px;
  color: #374151;
  line-height: 1.4;
}

.callout-text strong {
  color: #1f2937;
}

/* Zoom Modal */
.zoom-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 40px;
}

.zoom-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.zoom-close {
  position: absolute;
  top: -40px;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.15s;
}

.zoom-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.zoom-image {
  max-width: 100%;
  max-height: calc(90vh - 60px);
  border-radius: 8px;
}

.zoom-caption {
  margin-top: 12px;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
}

/* Zoom transition */
.zoom-enter-active,
.zoom-leave-active {
  transition: all 0.2s ease;
}

.zoom-enter-from,
.zoom-leave-to {
  opacity: 0;
}

.zoom-enter-from .zoom-content,
.zoom-leave-to .zoom-content {
  transform: scale(0.95);
}
</style>

<!-- Dark mode styles (unscoped) -->
<style>
[data-bs-theme="dark"] .screenshot-container {
  border-color: #334155;
  background: #1e293b;
}

[data-bs-theme="dark"] .screenshot-loading {
  color: #64748b;
}

[data-bs-theme="dark"] .toggle-btn {
  border-color: #334155;
  background: #1e293b;
  color: #94a3b8;
}

[data-bs-theme="dark"] .toggle-btn:hover {
  background: #334155;
}

[data-bs-theme="dark"] .toggle-btn.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}

[data-bs-theme="dark"] .screenshot-caption {
  color: #94a3b8;
}

[data-bs-theme="dark"] .screenshot-callouts {
  background: #1e293b;
  border-color: #334155;
}

[data-bs-theme="dark"] .callout-text {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .callout-text strong {
  color: #f1f5f9;
}
</style>
